"use client";

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  BookOpen,
  Boxes,
  Check,
  ChevronRight,
  Cpu,
  Database,
  FileText,
  FolderOpen,
  Network,
  Plus,
  Search,
  Server,
  Star,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import {
  kbDocCount,
  kbHasLiveProgress,
  kbNeedsReindex,
  kbProvider,
  resolveKbStatus,
  type KnowledgeBase,
  type ProgressInfo,
} from "@/lib/knowledge-helpers";
import type { RagProviderSummary } from "@/lib/knowledge-api";

interface KnowledgeHomeProps {
  kbs: KnowledgeBase[];
  providers: RagProviderSummary[];
  isLoading: boolean;
  kbLoadError: string | null;
  providerLoadError: string | null;
  onOpenKb: (name: string) => void;
  onOpenEngine: (id: string) => void;
  onCreate: () => void;
  /** Open the create flow pre-set to link an Obsidian vault. */
  onConnectObsidian: () => void;
}

const ENGINE_ICONS: Record<string, LucideIcon> = {
  llamaindex: Boxes,
  graphrag: Network,
  lightrag: Workflow,
  "lightrag-server": Server,
};

type EngineStatus = "ready" | "needs_key" | "unavailable";

type KnowledgeMapCardState =
  | { kind: "course"; documentCount: number | null }
  | {
      kind: "building";
      stage: string | null;
      message: string | null;
      percent: number | null;
    }
  | { kind: "error"; message: string | null };

function engineStatus(provider: RagProviderSummary): EngineStatus {
  if (provider.requires_api_key && provider.configured === false) {
    return "needs_key";
  }
  if (provider.configured === false) return "unavailable";
  return "ready";
}

function EngineStatusBadge({ status }: { status: EngineStatus }) {
  const { t } = useTranslation();
  if (status === "ready") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] bg-[var(--card)] px-1.5 py-0.5 text-[13px] font-medium text-[var(--m-mastered)]">
        <Check className="h-3 w-3" />
        {t("Ready")}
      </span>
    );
  }
  if (status === "needs_key") {
    return (
      <span className="inline-flex items-center rounded-full border border-[var(--border)] bg-[var(--card)] px-1.5 py-0.5 text-[13px] font-medium text-[var(--m-learning)]">
        {t("Needs key")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-[var(--border)] bg-[var(--muted)] px-1.5 py-0.5 text-[13px] font-medium text-[var(--muted-foreground)]">
      {t("Not installed")}
    </span>
  );
}

function StatusDot({ kb }: { kb: KnowledgeBase }) {
  const status = resolveKbStatus(kb);
  const needsReindex = kbNeedsReindex(kb);
  const isLive = kbHasLiveProgress(kb);
  const tone = needsReindex
    ? "bg-[var(--m-learning)]"
    : status === "error"
      ? "bg-[var(--destructive)]"
      : isLive
        ? "animate-pulse bg-[var(--spark)]"
        : status === "ready"
          ? "bg-[var(--m-mastered)]"
          : "bg-[var(--m-new)]";
  return <span className={`inline-block h-2 w-2 rounded-full ${tone}`} />;
}

function progressPercent(progress?: ProgressInfo): number | null {
  const direct = progress?.progress_percent ?? progress?.percent;
  if (typeof direct === "number") return Math.min(100, Math.max(0, direct));

  if (
    typeof progress?.current === "number" &&
    typeof progress.total === "number" &&
    progress.total > 0
  ) {
    return Math.min(
      100,
      Math.max(0, Math.round((progress.current / progress.total) * 100)),
    );
  }
  return null;
}

/**
 * KB list fields are the only data available to the Maps grid. Course
 * aggregates (concepts, mastery, counts, next target) deliberately stay out
 * until the server provides a KB/course summary DTO.
 */
export function knowledgeMapCardState(kb: KnowledgeBase): KnowledgeMapCardState {
  const status = resolveKbStatus(kb).toLowerCase();
  const progress = kb.progress;
  const stage = typeof progress?.stage === "string" ? progress.stage : null;
  const message =
    typeof progress?.message === "string" ? progress.message : null;

  if (status === "error" || status === "failed" || stage === "error") {
    return { kind: "error", message };
  }

  if (
    kbHasLiveProgress(kb) ||
    status === "initializing" ||
    status === "processing"
  ) {
    return {
      kind: "building",
      stage,
      message,
      percent: progressPercent(progress),
    };
  }

  return { kind: "course", documentCount: kbDocCount(kb) };
}

function LearningCourseCard({
  kb,
  onOpenKb,
}: {
  kb: KnowledgeBase;
  onOpenKb: (name: string) => void;
}) {
  const { t } = useTranslation();
  const state = knowledgeMapCardState(kb);
  if (state.kind !== "course") return null;

  return (
    <button
      type="button"
      onClick={() => onOpenKb(kb.name)}
      data-testid={`knowledge-course-${kb.name}`}
      className="group flex min-h-36 flex-col rounded-2xl border border-[var(--border)] bg-[var(--card)] p-4 text-left shadow-[var(--shadow-soft)] transition-[transform,border-color] duration-150 ease-[var(--ease)] hover:-translate-y-0.5 hover:border-[var(--ring)]"
    >
      <span className="grid h-8 w-8 place-items-center rounded-xl bg-[var(--muted)] text-[var(--primary)]">
        <BookOpen className="h-4 w-4" strokeWidth={1.8} />
      </span>
      <h2 className="mt-3 truncate text-[14px] font-semibold text-[var(--foreground)]">
        {kb.name}
      </h2>
      {state.documentCount !== null ? (
        <p className="mt-1 flex items-center gap-1.5 text-[13.5px] text-[var(--muted-foreground)]">
          <FileText className="h-3.5 w-3.5 shrink-0" />
          {t("knowledge.maps.sourceDocumentCount", {
            count: state.documentCount,
            defaultValue: "문서 {{count}}개",
          })}
        </p>
      ) : null}
    </button>
  );
}

function BuildingCard({ kb }: { kb: KnowledgeBase }) {
  const { t } = useTranslation();
  const state = knowledgeMapCardState(kb);
  if (state.kind !== "building") return null;

  return (
    <article
      data-testid={`knowledge-building-${kb.name}`}
      className="flex min-h-36 flex-col rounded-2xl border border-[var(--spark)] bg-[var(--card)] p-4 shadow-[var(--shadow-soft)]"
    >
      <div className="flex items-center gap-2 text-[13.5px] font-semibold text-[var(--foreground)]">
        <span className="h-2 w-2 rounded-full bg-[var(--spark)]" />
        {t("knowledge.maps.building", { defaultValue: "구축 중 Building" })}
      </div>
      <h2 className="mt-3 truncate text-[14px] font-semibold text-[var(--foreground)]">
        {kb.name}
      </h2>
      {state.stage ? (
        <p className="mt-1 text-[13.5px] text-[var(--muted-foreground)]">
          {state.stage}
        </p>
      ) : null}
      {state.message ? (
        <p className="mt-1 line-clamp-2 text-[13.5px] leading-relaxed text-[var(--muted-foreground)]">
          {state.message}
        </p>
      ) : null}
      {state.percent !== null ? (
        <div className="mt-auto pt-4">
          <div className="h-1.5 overflow-hidden rounded-full bg-[var(--muted)]">
            <div
              className="h-full rounded-full bg-[var(--spark)]"
              style={{ width: `${state.percent}%` }}
            />
          </div>
          <p className="mt-1.5 text-[13px] tabular-nums text-[var(--muted-foreground)]">
            {state.percent}%
          </p>
        </div>
      ) : null}
    </article>
  );
}

function FailedMapCard({
  kb,
  onOpenKb,
}: {
  kb: KnowledgeBase;
  onOpenKb: (name: string) => void;
}) {
  const { t } = useTranslation();
  const state = knowledgeMapCardState(kb);
  if (state.kind !== "error") return null;

  return (
    <button
      type="button"
      role="alert"
      onClick={() => onOpenKb(kb.name)}
      data-testid={`knowledge-error-${kb.name}`}
      className="flex min-h-36 flex-col rounded-2xl border border-[var(--destructive)] bg-[var(--card)] p-4 text-left shadow-[var(--shadow-soft)]"
    >
      <span className="flex items-center gap-2 text-[13.5px] font-semibold text-[var(--destructive)]">
        <AlertCircle className="h-3.5 w-3.5" />
        {t("knowledge.maps.buildFailed", {
          defaultValue: "지식맵 구축 실패",
        })}
      </span>
      <h2 className="mt-3 truncate text-[14px] font-semibold text-[var(--foreground)]">
        {kb.name}
      </h2>
      {state.message ? (
        <p className="mt-1 line-clamp-2 text-[13.5px] leading-relaxed text-[var(--muted-foreground)]">
          {state.message}
        </p>
      ) : null}
    </button>
  );
}

function NewCourseCard({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onCreate}
      data-testid="knowledge-create-course"
      className="flex min-h-36 flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--border)] bg-[var(--background)] p-4 text-center transition-colors hover:border-[var(--primary)]"
    >
      <span className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--muted)] text-[var(--primary)]">
        <Plus className="h-5 w-5" />
      </span>
      <span className="mt-3 text-[13.5px] font-semibold text-[var(--foreground)]">
        {t("knowledge.maps.createCourse", { defaultValue: "Create a new course" })}
      </span>
      <span className="mt-1 max-w-52 text-[13.5px] leading-relaxed text-[var(--muted-foreground)]">
        {t("knowledge.maps.createCourseDescription", {
          defaultValue: "문서를 넣으면 지식맵을 구축해 코스로 만듦",
        })}
      </span>
    </button>
  );
}

function LearningSkeleton() {
  const { t } = useTranslation();
  return (
    <div
      aria-label={t("Loading knowledge maps")}
      className="grid grid-cols-1 gap-4 md:grid-cols-3"
    >
      {Array.from({ length: 3 }, (_, index) => (
        <div
          key={index}
          className="min-h-36 animate-pulse rounded-2xl border border-[var(--border)] bg-[var(--card)] p-4"
        >
          <div className="h-8 w-8 rounded-xl bg-[var(--muted)]" />
          <div className="mt-3 h-4 w-2/3 rounded bg-[var(--muted)]" />
          <div className="mt-2 h-3 w-1/3 rounded bg-[var(--muted)]" />
        </div>
      ))}
    </div>
  );
}

export default function KnowledgeHome({
  kbs,
  providers,
  isLoading,
  kbLoadError,
  providerLoadError,
  onOpenKb,
  onOpenEngine,
  onCreate,
  onConnectObsidian,
}: KnowledgeHomeProps) {
  const { t } = useTranslation();
  const [hierarchy, setHierarchy] = useState<"learning" | "power">(
    "learning",
  );
  const [query, setQuery] = useState("");
  const providerName = (id: string) =>
    providers.find((provider) => provider.id === id)?.name ??
    id.charAt(0).toUpperCase() + id.slice(1);

  const obsidianCount = useMemo(
    () => kbs.filter((kb) => kb.metadata?.type === "obsidian").length,
    [kbs],
  );
  const kbCountByProvider = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const kb of kbs) {
      const provider = kbProvider(kb);
      counts[provider] = (counts[provider] ?? 0) + 1;
    }
    return counts;
  }, [kbs]);
  const filteredKbs = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return kbs;
    return kbs.filter((kb) => kb.name.toLowerCase().includes(normalized));
  }, [kbs, query]);
  const mapStates = useMemo(
    () => kbs.map((kb) => ({ kb, state: knowledgeMapCardState(kb) })),
    [kbs],
  );

  return (
    <div className="flex-1 overflow-y-auto bg-[var(--background)]">
      <div className="mx-auto max-w-[1024px] px-6 pb-8 pt-7 sm:px-0">
        <header>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-[20px] font-bold tracking-tight text-[var(--foreground)]">
              {t("knowledge.maps.title", {
                defaultValue: "지식맵 Knowledge maps",
              })}
            </h1>
            <p className="text-[13.5px] text-[var(--muted-foreground)]">
              {t("knowledge.maps.description", {
                defaultValue: "문서를 넣으면 그래프가 구축되고, 그 지식맵이 코스가 됨",
              })}
            </p>
          </div>
          <div
            role="tablist"
            aria-label={t("knowledge.maps.hierarchy", {
              defaultValue: "지식맵 위계",
            })}
            className="mt-4 flex h-11 w-full max-w-80 rounded-xl bg-[var(--muted)] p-0.5"
          >
            <button
              type="button"
              role="tab"
              aria-selected={hierarchy === "learning"}
              onClick={() => setHierarchy("learning")}
              className={`h-10 flex-1 rounded-[10px] px-3 text-[13.5px] font-semibold transition-colors ${
                hierarchy === "learning"
                  ? "bg-[var(--card)] text-[var(--primary)] shadow-[var(--shadow-soft)]"
                  : "text-[var(--muted-foreground)]"
              }`}
            >
              {t("knowledge.maps.learningHierarchy", {
                defaultValue: "학습 위계 Learning",
              })}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={hierarchy === "power"}
              onClick={() => setHierarchy("power")}
              className={`h-10 flex-1 rounded-[10px] px-3 text-[13.5px] font-semibold transition-colors ${
                hierarchy === "power"
                  ? "bg-[var(--card)] text-[var(--primary)] shadow-[var(--shadow-soft)]"
                  : "text-[var(--muted-foreground)]"
              }`}
            >
              {t("knowledge.maps.powerHierarchy", {
                defaultValue: "파워유저 위계 Power user",
              })}
            </button>
          </div>
        </header>

        {hierarchy === "learning" ? (
          <section className="mt-6" aria-labelledby="knowledge-maps-grid-title">
            <h2 id="knowledge-maps-grid-title" className="sr-only">
              {t("knowledge.maps.title", {
                defaultValue: "지식맵 Knowledge maps",
              })}
            </h2>
            {kbLoadError ? (
              <div
                role="alert"
                className="rounded-2xl border border-[var(--destructive)] bg-[var(--card)] px-4 py-3 text-[13.5px] text-[var(--destructive)]"
              >
                {t("knowledge.maps.loadFailed", {
                  defaultValue: "지식맵을 불러오지 못했습니다.",
                })}{" "}
                {kbLoadError}
              </div>
            ) : isLoading ? (
              <LearningSkeleton />
            ) : mapStates.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[var(--border)] px-4 py-10 text-center">
                <Database className="mx-auto h-6 w-6 text-[var(--muted-foreground)]" />
                <p className="mt-2 text-[13.5px] font-medium text-[var(--foreground)]">
                  {t("knowledge.maps.emptyTitle", {
                    defaultValue: "아직 지식맵이 없습니다",
                  })}
                </p>
                <p className="mx-auto mt-1 max-w-sm text-[13.5px] leading-relaxed text-[var(--muted-foreground)]">
                  {t("knowledge.maps.emptyDescription", {
                    defaultValue: "책을 만들면 첫 코스를 시작할 수 있습니다.",
                  })}
                </p>
                <button
                  type="button"
                  onClick={onCreate}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[var(--primary)] px-3.5 py-2 text-[13.5px] font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-90"
                >
                  <Plus className="h-4 w-4" />
                  {t("knowledge.maps.createCourse", {
                    defaultValue: "Create a new course",
                  })}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {mapStates.map(({ kb, state }) => {
                  if (state.kind === "building") {
                    return <BuildingCard key={kb.name} kb={kb} />;
                  }
                  if (state.kind === "error") {
                    return (
                      <FailedMapCard
                        key={kb.name}
                        kb={kb}
                        onOpenKb={onOpenKb}
                      />
                    );
                  }
                  return (
                    <LearningCourseCard
                      key={kb.name}
                      kb={kb}
                      onOpenKb={onOpenKb}
                    />
                  );
                })}
                <NewCourseCard onCreate={onCreate} />
              </div>
            )}
          </section>
        ) : (
          <div className="mt-8">
            <section>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-[14px] font-semibold text-[var(--foreground)]">
                  {t("knowledge.maps.management", {
                    defaultValue: "책 관리",
                  })}
                </h2>
                <button
                  type="button"
                  onClick={onCreate}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--primary)] px-4 py-2.5 text-[13.5px] font-semibold text-[var(--primary-foreground)] shadow-[var(--shadow-soft)] transition-opacity hover:opacity-90"
                >
                  <Plus className="h-4 w-4" strokeWidth={2} />
                  {t("New knowledge base")}
                </button>
              </div>

              <h3 className="mb-3 flex items-center gap-2 text-[13px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
                <Cpu className="h-3.5 w-3.5" />
                {t("Retrieval engines")}
              </h3>
              {providerLoadError ? (
                <div
                  role="alert"
                  className="mb-3 rounded-xl border border-[var(--destructive)] bg-[var(--card)] px-3 py-2 text-[13.5px] text-[var(--destructive)]"
                >
                  {t("knowledge.maps.providerLoadFailed", {
                    defaultValue: "검색 엔진 목록을 불러오지 못했습니다.",
                  })}{" "}
                  {providerLoadError}
                </div>
              ) : null}
              <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-3">
                {providerLoadError
                  ? null
                  : providers.map((provider) => {
                      const status = engineStatus(provider);
                      const Icon = ENGINE_ICONS[provider.id] ?? Boxes;
                      const count = kbCountByProvider[provider.id] ?? 0;
                      return (
                        <button
                          key={provider.id}
                          type="button"
                          onClick={() => onOpenEngine(provider.id)}
                          className="group flex flex-col gap-2 rounded-2xl border border-[var(--border)] bg-[var(--card)] p-4 text-left shadow-[var(--shadow-soft)] transition-[transform,border-color] duration-150 ease-[var(--ease)] hover:-translate-y-0.5 hover:border-[var(--ring)]"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex min-w-0 items-center gap-2">
                              <Icon
                                className="h-4 w-4 shrink-0 text-[var(--muted-foreground)]"
                                strokeWidth={1.7}
                              />
                              <span className="truncate text-[13.5px] font-medium text-[var(--foreground)]">
                                {provider.name}
                              </span>
                            </div>
                            <EngineStatusBadge status={status} />
                          </div>
                          <p className="line-clamp-2 text-[13.5px] leading-snug text-[var(--muted-foreground)]">
                            {provider.description}
                          </p>
                          <div className="mt-auto flex items-center gap-2 pt-1 text-[13px] text-[var(--muted-foreground)]">
                            {provider.modes &&
                            provider.modes.length > 0 &&
                            provider.default_mode ? (
                              <span className="rounded-full border border-[var(--border)] px-1.5 py-0.5 font-mono">
                                {provider.default_mode}
                              </span>
                            ) : null}
                            {count > 0 ? (
                              <span>{t("{{count}} KB", { count })}</span>
                            ) : null}
                            <ChevronRight className="ml-auto h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-60" />
                          </div>
                        </button>
                      );
                    })}

                <button
                  type="button"
                  onClick={onConnectObsidian}
                  className="group flex flex-col gap-2 rounded-2xl border border-[var(--border)] bg-[var(--card)] p-4 text-left shadow-[var(--shadow-soft)] transition-[transform,border-color] duration-150 ease-[var(--ease)] hover:-translate-y-0.5 hover:border-[var(--ring)]"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <FolderOpen
                        className="h-4 w-4 shrink-0 text-[var(--muted-foreground)]"
                        strokeWidth={1.7}
                      />
                      <span className="truncate text-[13.5px] font-medium text-[var(--foreground)]">
                        {t("Obsidian")}
                      </span>
                    </div>
                    {obsidianCount > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] bg-[var(--card)] px-1.5 py-0.5 text-[13px] font-medium text-[var(--m-mastered)]">
                        <Check className="h-3 w-3" />
                        {t("{{count}} connected", { count: obsidianCount })}
                      </span>
                    ) : null}
                  </div>
                  <p className="line-clamp-2 text-[13.5px] leading-snug text-[var(--muted-foreground)]">
                    {t(
                      "Connect your Obsidian vault. The tutor navigates and writes notes in place — no upload, no index. Local / self-hosted only.",
                    )}
                  </p>
                  <div className="mt-auto flex items-center gap-2 pt-1 text-[13px] text-[var(--muted-foreground)]">
                    <span className="inline-flex items-center gap-1">
                      <FolderOpen className="h-3 w-3" />
                      {t("Connect vault")}
                    </span>
                    <ChevronRight className="ml-auto h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-60" />
                  </div>
                </button>
              </div>
            </section>

            <section className="mt-8 pb-2">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-[13px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
                  <Database className="h-3.5 w-3.5" />
                  {t("Knowledge bases")}
                  {!kbLoadError ? (
                    <span className="rounded-full bg-[var(--muted)] px-1.5 py-0.5 text-[13px] text-[var(--muted-foreground)]">
                      {kbs.length}
                    </span>
                  ) : null}
                </h2>
                {kbs.length > 6 && !kbLoadError ? (
                  <div className="relative w-48">
                    <Search
                      className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--muted-foreground)]"
                      aria-hidden
                    />
                    <input
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder={t("Search knowledge bases…")}
                      className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] py-1.5 pl-8 pr-3 text-[13.5px] text-[var(--foreground)] outline-none transition-colors placeholder:text-[var(--muted-foreground)] focus:border-[var(--ring)]"
                    />
                  </div>
                ) : null}
              </div>

              {kbLoadError ? (
                <div
                  role="alert"
                  className="rounded-2xl border border-[var(--destructive)] bg-[var(--card)] px-4 py-3 text-[13.5px] text-[var(--destructive)]"
                >
                  {t("knowledge.maps.loadFailed", {
                    defaultValue: "지식맵을 불러오지 못했습니다.",
                  })}{" "}
                  {kbLoadError}
                </div>
              ) : isLoading ? (
                <LearningSkeleton />
              ) : kbs.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[var(--border)] px-4 py-12 text-center">
                  <Database className="mx-auto mb-2 h-6 w-6 text-[var(--muted-foreground)]" />
                  <div className="text-[13.5px] font-medium text-[var(--foreground)]">
                    {t("No knowledge bases yet")}
                  </div>
                  <p className="mx-auto mt-1 max-w-sm text-[13.5px] leading-relaxed text-[var(--muted-foreground)]">
                    {t(
                      "Create one to upload documents and retrieve grounded context in chat.",
                    )}
                  </p>
                  <button
                    type="button"
                    onClick={onCreate}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[var(--primary)] px-3.5 py-2 text-[13.5px] font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-90"
                  >
                    <Plus size={14} />
                    {t("New knowledge base")}
                  </button>
                </div>
              ) : filteredKbs.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[var(--border)] px-4 py-8 text-center text-[13.5px] text-[var(--muted-foreground)]">
                  {t("No matches")}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {filteredKbs.map((kb) => {
                    const docs = kbDocCount(kb);
                    return (
                      <button
                        key={kb.name}
                        type="button"
                        onClick={() => onOpenKb(kb.name)}
                        className="group flex flex-col gap-2 rounded-2xl border border-[var(--border)] bg-[var(--card)] p-4 text-left shadow-[var(--shadow-soft)] transition-[transform,border-color] duration-150 ease-[var(--ease)] hover:-translate-y-0.5 hover:border-[var(--ring)]"
                      >
                        <div className="flex items-center gap-2">
                          <StatusDot kb={kb} />
                          <span className="truncate text-[13.5px] font-medium text-[var(--foreground)]">
                            {kb.name}
                          </span>
                          {kb.is_default ? (
                            <Star
                              className="h-3 w-3 shrink-0 text-[var(--m-learning)]"
                              fill="currentColor"
                            />
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-[13px] text-[var(--muted-foreground)]">
                          <span className="rounded-full border border-[var(--border)] px-1.5 py-0.5">
                            {providerName(kbProvider(kb))}
                          </span>
                          {docs !== null ? (
                            <span>
                              {docs} {t("docs")}
                            </span>
                          ) : null}
                          <span>{resolveKbStatus(kb)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
